// ============================================================================
// Time & Labor — workers & caps admin (package-one minimum: what the notional
// test needs). Workers (create + PIN), assignments (facility XOR market, with
// the per-worker authorized-hours cap), and facility line caps.
//
// ⛔ There is NO overtime control here and none may be added (D-01) — the
//    per-worker OT override is the mechanism that suppressed OT for 117
//    people; the OT rules live inert in labor_rules until ruled active.
// ============================================================================

import { canAdministerTime, canAssignWorkers, hasFullAmAuthority } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { supabase } from '../../shared/supabase';
import { useTimeTabsNav } from './time-console';
import { fmtWhen } from './console-parts';
import { clockUrlSpoken, joiningMessage } from './joining-message';
import {
  createAssignment,
  createWorker,
  setWorkerLanguage,
  setWorkerPin,
  unbindDevice,
  useDevices,
  useEmployerOptions,
  useFacilityOptions,
  useWorkerPersonGroups,
  useServiceLines,
  useWorkersAdmin,
  verifyDevice,
} from './hooks/use-time-data';
import type { WorkerRow } from './types';
import { Link } from 'react-router-dom';
import { personCardPath } from '../hr/directory-everyone';

const CLASS_LABELS: Record<string, string> = {
  full_employee: 'Full Employee',
  hourly_paid_employee: 'Hourly Paid Employee',
  individual_1099: '1099 Individual',
  subcontractor: 'Subcontractor (entity)',
  subcontractor_crew: "Subcontractor's crew",
};
const CREW = 'subcontractor_crew';

const LANGUAGES = [
  ['es', 'Español'], ['en', 'English'], ['fr', 'Français'], ['ht', 'Kreyòl'], ['wo', 'Wolof'],
] as const;

/** A worker's name, opening their People directory card — the same card the directory opens. */
function WorkerName({ group, name }: { group: string | undefined; name: string }) {
  if (!group) return <>{name}</>;
  return (
    <Link to={personCardPath(group)} className="font-medium text-tcs-accent-ink underline underline-offset-2">
      {name}
    </Link>
  );
}

export function TimeAdmin() {
  const { profile } = useAuth();
  const tabsNav = useTimeTabsNav('admin');
  const { workers, assignments, loading, error, reload } = useWorkersAdmin();
  const facilities = useFacilityOptions();
  const serviceLines = useServiceLines();
  const employers = useEmployerOptions();
  const employerName = useMemo(
    () => new Map(employers.rows.map((e) => [e.provider_id, e.display])),
    [employers.rows],
  );

  const role = profile?.role ?? 'none';
  const isHr = canAdministerTime(role);
  const canAssign = canAssignWorkers(role);
  // A new punch code mirrors rpc_time_set_worker_pin, which also admits
  // has_full_am_authority() — the national AM sees this tab (Ben, 2026-10-26, #981).
  const canSetPin = isHr || hasFullAmAuthority(role);

  const [status, setStatus] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // ----- new worker form -----
  const [wFirst, setWFirst] = useState('');
  const [wLast, setWLast] = useState('');
  const [wPhone, setWPhone] = useState('');
  const [wLang, setWLang] = useState('es');
  const [wClass, setWClass] = useState('hourly_paid_employee');
  // Works for: '' = TCS. Choosing a company makes the type Subcontractor's crew and
  // locks it; the database refuses the two disagreeing (workers_crew_has_employer).
  const [wEmployer, setWEmployer] = useState('');
  const [wPin, setWPin] = useState('');
  const [wState, setWState] = useState('');
  const [wBusy, setWBusy] = useState(false);

  // ----- PIN dialog -----
  const [pinWorker, setPinWorker] = useState<WorkerRow | null>(null);
  const [pinValue, setPinValue] = useState('');
  const [pinBusy, setPinBusy] = useState(false);

  // ----- new assignment form -----
  const [aWorker, setAWorker] = useState('');
  const [aKind, setAKind] = useState<'facility' | 'market'>('facility');
  const [aFacility, setAFacility] = useState('');
  const [aMarket, setAMarket] = useState('');
  const [aLine, setALine] = useState('evs');
  const [aShift, setAShift] = useState('');
  const [aCap, setACap] = useState('');
  const [aSchedHours, setASchedHours] = useState('');
  const [aBreakMin, setABreakMin] = useState('');
  const [aBusy, setABusy] = useState(false);

  // ----- devices (TCSD-0140: HR confirms every worker-phone pairing) -----
  const devices = useDevices();
  const [devBusy, setDevBusy] = useState<string | null>(null);

  // Hours-report links moved to their own tab, Time & Labor → Hours links (Ben, 2026-10-23).
  // A worker's name opens their People directory card (Ben, 2026-10-23).
  const personGroups = useWorkerPersonGroups();

  // ----- the language we speak to a worker in -----
  const [langBusy, setLangBusy] = useState<string | null>(null);
  const [pinCopied, setPinCopied] = useState(false);

  // ----- facility cap form -----
  const [cFacility, setCFacility] = useState('');
  const [cLine, setCLine] = useState('evs');
  const [cHours, setCHours] = useState('');
  const [cBusy, setCBusy] = useState(false);

  const assignmentsByWorker = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of assignments) m.set(a.worker_id, (m.get(a.worker_id) ?? 0) + 1);
    return m;
  }, [assignments]);

  const facilityDisplay = useMemo(
    () => new Map(facilities.rows.map((f) => [f.facility_id, f.display])),
    [facilities.rows],
  );

  async function submitWorker() {
    setWBusy(true); setFormError(null); setStatus(null);
    try {
      const id = await createWorker({
        firstName: wFirst.trim(), lastName: wLast.trim(),
        phoneMobile: wPhone.trim() || undefined,
        preferredLanguage: wLang,
        employmentClass: wEmployer ? CREW : wClass,
        employerProviderId: wEmployer || null,
        workState: wState.trim() || undefined,
        createdByEid: profile?.employee_id ?? null,
      });
      // The PIN goes through the one door that sets a PIN (rpc_time_set_worker_pin).
      if (wPin) {
        try {
          await setWorkerPin(id, wPin);
        } catch (e) {
          setStatus(`Worker ${id} created.`);
          setFormError(`The PIN was not set: ${errorMessage(e, 'Could not set the PIN.')} Use Set PIN… on the row.`);
          setWFirst(''); setWLast(''); setWPhone(''); setWPin('');
          await reload();
          return;
        }
      }
      setStatus(wPin ? `Worker ${id} created, PIN set.` : `Worker ${id} created.`);
      setWFirst(''); setWLast(''); setWPhone(''); setWPin('');
      await reload();
    } catch (e) {
      setFormError(errorMessage(e, 'Could not create the worker.'));
    } finally {
      setWBusy(false);
    }
  }

  /**
   * ⛔ Caught HERE, where the click happened, so the row keeps its state and
   * the person is told — never routed to page-level error state, which would
   * unmount the table holding the evidence (PLAYBOOK §15.1).
   */
  async function onSetLanguage(workerId: string, language: 'es' | 'en') {
    setLangBusy(workerId); setFormError(null); setStatus(null);
    try {
      await setWorkerLanguage(workerId, language);
      await reload();
    } catch (e) {
      setFormError(errorMessage(e, 'Could not change the language for that worker.'));
    } finally {
      setLangBusy(null);
    }
  }

  async function submitPin() {
    if (!pinWorker) return;
    setPinBusy(true); setFormError(null);
    try {
      await setWorkerPin(pinWorker.worker_id, pinValue);
      setStatus(`PIN set for ${`${pinWorker.first_name} ${pinWorker.last_name}`.trim()}.`);
      setPinWorker(null); setPinValue('');
      await reload();
    } catch (e) {
      setFormError(errorMessage(e, 'Could not set the PIN.'));
    } finally {
      setPinBusy(false);
    }
  }

  async function submitAssignment() {
    setABusy(true); setFormError(null); setStatus(null);
    try {
      const id = await createAssignment({
        workerId: aWorker,
        facilityId: aKind === 'facility' ? aFacility : null,
        marketRegion: aKind === 'market' ? aMarket.trim() : null,
        serviceLineCode: aLine,
        shiftCode: aShift || null,
        authorizedHoursPerWeek: aCap.trim() === '' ? null : Number(aCap),
        scheduledHoursPerShift: aSchedHours.trim() === '' ? null : Number(aSchedHours),
        unpaidBreakMinutes: aBreakMin.trim() === '' ? 0 : Number(aBreakMin),
        createdByEid: profile?.employee_id ?? null,
      });
      setStatus(`Assignment ${id} created.`);
      setACap(''); setASchedHours(''); setABreakMin('');
      await reload();
    } catch (e) {
      setFormError(errorMessage(e, 'Could not create the assignment.'));
    } finally {
      setABusy(false);
    }
  }

  async function submitFacilityCap() {
    setCBusy(true); setFormError(null); setStatus(null);
    try {
      const { error: err } = await supabase.from('facility_service_lines').insert({
        facility_id: cFacility,
        service_line_code: cLine,
        authorized_hours_per_week: Number(cHours),
        created_by_eid: profile?.employee_id ?? null,
      });
      if (err) throw err;
      setStatus(`Cap saved: ${facilityDisplay.get(cFacility) ?? cFacility} · ${cLine} · ${cHours} h/wk.`);
      setCHours('');
    } catch (e) {
      setFormError(errorMessage(e, 'Could not save the facility cap.'));
    } finally {
      setCBusy(false);
    }
  }

  async function submitVerifyDevice(deviceId: string) {
    setDevBusy(deviceId); setFormError(null);
    try {
      await verifyDevice(deviceId, profile?.employee_id ?? null);
      await devices.reload();
    } catch (e) {
      setFormError(errorMessage(e, 'Could not confirm the device.'));
    } finally { setDevBusy(null); }
  }

  async function submitUnbindDevice(deviceId: string) {
    setDevBusy(deviceId); setFormError(null);
    try {
      await unbindDevice(deviceId);
      await devices.reload();
    } catch (e) {
      setFormError(errorMessage(e, 'Could not unbind the device.'));
    } finally { setDevBusy(null); }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="Workers & caps"
        meta={loading ? 'Loading…' : `${workers.length} workers · ${assignments.length} assignments · authorized-hours caps flag, they never block a punch`}
        actions={<Button size="sm" variant="secondary" onClick={() => void reload()}>Refresh</Button>}
      />
      <div className="mt-3"><TabBar {...tabsNav} /></div>

      {(error || formError || employers.error) && (
        <div className="mt-4 rounded-xl border border-st-danger-border bg-st-danger-bg px-4 py-3 text-sm text-st-danger-fg">
          {error ?? formError ?? employers.error}
        </div>
      )}
      {status && (
        <div className="mt-4 rounded-xl border border-st-ok-border bg-st-ok-bg px-4 py-3 text-sm text-st-ok-fg">
          {status}
        </div>
      )}

      {/* ----- workers ----- */}
      <section className="mt-5">
        <h2 className="text-sm font-bold uppercase tracking-wide text-tcs-text-muted">Workers</h2>
        <div className="mt-2 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">Worker</th>
                <th className="px-3 py-2 font-semibold">Class</th>
                <th className="px-3 py-2 font-semibold">Works for</th>
                <th className="px-3 py-2 font-semibold">Language</th>
                <th className="px-3 py-2 font-semibold">Phone</th>
                <th className="px-3 py-2 text-right font-semibold">Assignments</th>
                <th className="px-3 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {workers.map((w) => (
                <tr key={w.worker_id} className="border-t border-tcs-border bg-tcs-raised">
                  <td className="px-3 py-2 font-medium text-tcs-text">
                    <WorkerName group={personGroups.get(w.worker_id)} name={`${w.first_name} ${w.last_name}`} />
                    <span className="ml-2 text-xs text-tcs-text-muted">{w.worker_id}</span>
                    {w.linked_provider_id && (
                      <span className="ml-2 rounded-full border border-st-info-border bg-st-info-bg px-2 py-0.5 text-[10.5px] font-semibold text-st-info-fg">
                        owns {w.linked_provider_id} — second approver required
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{CLASS_LABELS[w.employment_class] ?? w.employment_class}</td>
                  <td className="px-3 py-2 text-tcs-text-secondary">
                    {w.employer_provider_id ? (employerName.get(w.employer_provider_id) ?? w.employer_provider_id) : 'TCS'}
                  </td>
                  {/* ⛔ The language is a fact about the PERSON, and this is the
                      same column the clock page writes through to when they
                      press the switch themselves. One fact, one place. */}
                  <td className="px-3 py-2">
                    {isHr ? (
                      <div className="flex gap-1">
                        {(['es', 'en'] as const).map((l) => (
                          <button key={l} type="button"
                            disabled={langBusy === w.worker_id}
                            onClick={() => void onSetLanguage(w.worker_id, l)}
                            className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                              w.preferred_language === l
                                ? 'bg-tcs-inverse text-tcs-inverse-ink'
                                : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text-secondary hover:bg-tcs-surface'}`}>
                            {l === 'es' ? 'Español' : 'English'}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="text-tcs-text-secondary">
                        {w.preferred_language === 'en' ? 'English' : 'Español'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{w.phone_mobile ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{assignmentsByWorker.get(w.worker_id) ?? 0}</td>
                  <td className="px-3 py-2 text-right">
                    {canSetPin && (
                      <button
                        type="button"
                        onClick={() => { setPinWorker(w); setPinValue(''); }}
                        className="rounded-lg border border-tcs-border-strong bg-tcs-bg px-3 py-1.5 text-xs font-semibold text-tcs-text hover:bg-tcs-surface"
                      >
                        Set PIN…
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isHr && (
          <div className="mt-3 flex flex-wrap items-end gap-2 rounded-xl border border-tcs-border bg-tcs-raised p-4">
            <div className="text-sm font-semibold text-tcs-text">Add worker</div>
            <input value={wFirst} onChange={(e) => setWFirst(e.target.value)} placeholder="First name"
              className="h-9 w-36 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="First name" />
            {/* A sub's cleaner may come with a first name only ("as the sub gives it" — the approved demo). */}
            <input value={wLast} onChange={(e) => setWLast(e.target.value)} placeholder={wEmployer ? 'Last name (if known)' : 'Last name'}
              className="h-9 w-36 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Last name" />
            <input value={wPhone} onChange={(e) => setWPhone(e.target.value)} placeholder="Mobile (identity)"
              className="h-9 w-40 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Mobile phone" />
            <select value={wLang} onChange={(e) => setWLang(e.target.value)}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Language">
              {LANGUAGES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs text-tcs-text-secondary">
              Works for
              <select value={wEmployer} onChange={(e) => setWEmployer(e.target.value)}
                className="h-9 max-w-[18rem] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text" aria-label="Works for">
                <option value="">TCS — paid by TCS</option>
                {employers.rows.map((e) => <option key={e.provider_id} value={e.provider_id}>{e.display}</option>)}
              </select>
            </label>
            <select value={wEmployer ? CREW : wClass} onChange={(e) => setWClass(e.target.value)}
              disabled={!!wEmployer}
              title={wEmployer ? "Set by Works for: someone who works for a subcontractor is Subcontractor's crew." : undefined}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm disabled:opacity-70" aria-label="Worker class">
              {Object.entries(CLASS_LABELS)
                .filter(([code]) => (wEmployer ? code === CREW : code !== CREW))
                .map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
            <input value={wPin} onChange={(e) => setWPin(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="PIN (optional)"
              inputMode="numeric" autoComplete="off"
              className="h-9 w-32 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="PIN" />
            <input value={wState} onChange={(e) => setWState(e.target.value.toUpperCase().slice(0, 2))} placeholder="State"
              className="h-9 w-16 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Work state" />
            <Button size="sm" loading={wBusy}
              disabled={wFirst.trim() === '' || (wLast.trim() === '' && !wEmployer) || (wPin !== '' && wPin.length < 4)}
              onClick={() => void submitWorker()}>
              Add worker
            </Button>
          </div>
        )}
      </section>

      {/* ----- assignments ----- */}
      <section className="mt-6">
        <h2 className="text-sm font-bold uppercase tracking-wide text-tcs-text-muted">Assignments & worker caps</h2>
        <div className="mt-2 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">Worker</th>
                <th className="px-3 py-2 font-semibold">Scope</th>
                <th className="px-3 py-2 font-semibold">Line</th>
                <th className="px-3 py-2 font-semibold">Shift</th>
                <th className="px-3 py-2 text-right font-semibold">Authorized h/wk</th>
                <th className="px-3 py-2 text-right font-semibold">Scheduled h/shift</th>
                <th className="px-3 py-2 text-right font-semibold">Unpaid break</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => {
                const w = workers.find((x) => x.worker_id === a.worker_id);
                return (
                  <tr key={a.assignment_id} className="border-t border-tcs-border bg-tcs-raised">
                    <td className="px-3 py-2 font-medium text-tcs-text">{w ? <WorkerName group={personGroups.get(w.worker_id)} name={`${w.first_name} ${w.last_name}`} /> : a.worker_id}</td>
                    <td className="px-3 py-2 text-tcs-text-secondary">
                      {a.facility_id ? (facilityDisplay.get(a.facility_id) ?? a.facility_id) : `Market: ${a.market_region}`}
                    </td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{a.service_line_code}</td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{a.shift_code ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.authorized_hours_per_week ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.scheduled_hours_per_shift ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.unpaid_break_minutes ? `${a.unpaid_break_minutes} min` : '—'}</td>
                  </tr>
                );
              })}
              {assignments.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-tcs-text-muted">
                  No assignments yet — add one below so punches default correctly and the caps have something to check.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {canAssign && (
          <div className="mt-3 flex flex-wrap items-end gap-2 rounded-xl border border-tcs-border bg-tcs-raised p-4">
            <div className="w-full text-sm font-semibold text-tcs-text">Add assignment</div>
            <select value={aWorker} onChange={(e) => setAWorker(e.target.value)}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Worker">
              <option value="">Worker…</option>
              {workers.filter((w) => w.worker_status === 'active').map((w) => (
                <option key={w.worker_id} value={w.worker_id}>{w.first_name} {w.last_name}</option>
              ))}
            </select>
            <select value={aKind} onChange={(e) => setAKind(e.target.value as 'facility' | 'market')}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Scope kind">
              <option value="facility">Facility</option>
              <option value="market">Market (roving)</option>
            </select>
            {aKind === 'facility' ? (
              <select value={aFacility} onChange={(e) => setAFacility(e.target.value)}
                className="h-9 max-w-[260px] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Facility">
                <option value="">Facility…</option>
                {facilities.rows.map((f) => <option key={f.facility_id} value={f.facility_id}>{f.display}</option>)}
              </select>
            ) : (
              <input value={aMarket} onChange={(e) => setAMarket(e.target.value)} placeholder="Market, e.g. Texas"
                className="h-9 w-40 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Market region" />
            )}
            <select value={aLine} onChange={(e) => setALine(e.target.value)}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Service line">
              {serviceLines.map((l) => <option key={l.service_line_code} value={l.service_line_code}>{l.label}</option>)}
            </select>
            <select value={aShift} onChange={(e) => setAShift(e.target.value)}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Shift">
              <option value="">Shift…</option>
              <option value="1st">1st</option><option value="2nd">2nd</option><option value="3rd">3rd</option>
              <option value="night">Night</option><option value="weekend">Weekend</option><option value="flex">Flex</option>
            </select>
            <input value={aCap} onChange={(e) => setACap(e.target.value.replace(/[^\d.]/g, ''))} placeholder="Auth h/wk"
              className="h-9 w-24 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Authorized hours per week" />
            <input value={aSchedHours} onChange={(e) => setASchedHours(e.target.value.replace(/[^\d.]/g, ''))} placeholder="Sched h/shift"
              className="h-9 w-28 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Scheduled hours per shift" />
            <input value={aBreakMin} onChange={(e) => setABreakMin(e.target.value.replace(/\D/g, ''))} placeholder="Break min"
              className="h-9 w-24 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Unpaid break minutes" />
            <Button size="sm" loading={aBusy}
              disabled={aWorker === '' || (aKind === 'facility' ? aFacility === '' : aMarket.trim() === '')}
              onClick={() => void submitAssignment()}>
              Add assignment
            </Button>
            <div className="w-full text-xs text-tcs-text-muted">
              Scheduled hours per shift is the auto-flag anchor: an 8-hour shift with a 60-minute unpaid
              lunch spans 9 hours, and anything above that span flags for review. Leave it blank where the
              schedule is unknown — unknown never flags.
            </div>
          </div>
        )}
      </section>

      {/* ----- facility caps ----- */}
      {canAssign && (
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-tcs-text-muted">Facility weekly caps (R-05)</h2>
          <div className="mt-2 flex flex-wrap items-end gap-2 rounded-xl border border-tcs-border bg-tcs-raised p-4">
            <select value={cFacility} onChange={(e) => setCFacility(e.target.value)}
              className="h-9 max-w-[280px] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Facility for cap">
              <option value="">Facility…</option>
              {facilities.rows.map((f) => <option key={f.facility_id} value={f.facility_id}>{f.display}</option>)}
            </select>
            <select value={cLine} onChange={(e) => setCLine(e.target.value)}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm" aria-label="Service line for cap">
              {serviceLines.map((l) => <option key={l.service_line_code} value={l.service_line_code}>{l.label}</option>)}
            </select>
            <input value={cHours} onChange={(e) => setCHours(e.target.value.replace(/[^\d.]/g, ''))} placeholder="Hours/week"
              className="h-9 w-28 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm" aria-label="Cap hours per week" />
            <Button size="sm" loading={cBusy} disabled={cFacility === '' || cHours.trim() === ''}
              onClick={() => void submitFacilityCap()}>
              Save cap
            </Button>
            <div className="w-full text-xs text-tcs-text-muted">
              “If Menorah says we only want 100 hours per week, I need to know if it exceeds 100 hours.”
              Going over flags HR and the AM — it never blocks a punch.
            </div>
          </div>
        </section>
      )}

      {/* ----- worker devices (TCSD-0140) ----- */}
      {isHr && (
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-tcs-text-muted">Worker phones & devices</h2>
          <div className="mt-1 text-xs text-tcs-text-secondary">
            A worker's first punch from their own phone binds it here at reduced trust, and confirming
            the pairing is what raises that trust on the daily review. ⛔ It no longer gates anything a
            worker can see — they read their own hours with their number and PIN, on any phone
            (Ben, 2026-09-22). Unbind a phone that changed hands; the worker's next punch re-binds their new one.
          </div>
          <div className="mt-2 overflow-x-auto rounded-xl border border-tcs-border">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                  <th className="px-3 py-2 font-semibold">Device</th>
                  <th className="px-3 py-2 font-semibold">Bound to</th>
                  <th className="px-3 py-2 font-semibold">First seen</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {devices.rows.map((d) => {
                  const w = d.bound_worker_id ? workers.find((x) => x.worker_id === d.bound_worker_id) : null;
                  const pendingVerify = d.bound_worker_id != null && d.verified_at == null;
                  return (
                    <tr key={d.device_id} className="border-t border-tcs-border bg-tcs-raised">
                      <td className="px-3 py-2 font-medium text-tcs-text">{d.friendly_name}</td>
                      <td className="px-3 py-2 text-tcs-text-secondary">
                        {w ? `${w.first_name} ${w.last_name}` : d.bound_facility_id ? `Facility device (${facilityDisplay.get(d.bound_facility_id) ?? d.bound_facility_id})` : '—'}
                      </td>
                      <td className="px-3 py-2 text-tcs-text-secondary">{fmtWhen(d.first_seen_at)}</td>
                      <td className="px-3 py-2">
                        {pendingVerify ? (
                          <span className="rounded-full border border-st-warn-border bg-st-warn-bg px-2 py-0.5 text-[11px] font-semibold text-st-warn-fg">
                            awaiting your confirmation
                          </span>
                        ) : d.verified_at ? (
                          <span className="rounded-full border border-st-ok-border bg-st-ok-bg px-2 py-0.5 text-[11px] font-semibold text-st-ok-fg">confirmed</span>
                        ) : (
                          <span className="rounded-full border border-tcs-border bg-tcs-surface px-2 py-0.5 text-[11px] font-semibold text-tcs-text-muted">{d.trust_tier}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {d.bound_worker_id && (
                          <span className="inline-flex gap-2">
                            {pendingVerify && (
                              <button type="button" disabled={devBusy === d.device_id}
                                onClick={() => void submitVerifyDevice(d.device_id)}
                                className="rounded-lg bg-tcs-inverse px-3 py-1.5 text-xs font-semibold text-tcs-inverse-ink hover:bg-tcs-inverse-hover">
                                Confirm pairing
                              </button>
                            )}
                            <button type="button" disabled={devBusy === d.device_id}
                              onClick={() => void submitUnbindDevice(d.device_id)}
                              className="rounded-lg border border-tcs-border-strong bg-tcs-bg px-3 py-1.5 text-xs font-semibold text-tcs-text hover:bg-tcs-surface">
                              Unbind
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {devices.rows.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-tcs-text-muted">
                    No devices yet. A worker's first punch from their phone lands here for your confirmation.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {devices.error && <div className="mt-2 text-sm text-st-danger-fg">{devices.error}</div>}
        </section>
      )}

      {/* ----- PIN dialog ----- */}
      {pinWorker && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim p-4 sm:items-center">
          <div className="w-full max-w-sm rounded-2xl border border-tcs-border-strong bg-tcs-overlay shadow-xl">
            <div className="border-b border-tcs-border px-5 py-3 font-semibold text-tcs-text">
              Set kiosk PIN — {`${pinWorker.first_name} ${pinWorker.last_name}`.trim()}
            </div>
            <div className="flex flex-col gap-3 px-5 py-4">
              <div className="text-xs text-tcs-text-secondary">
                4–6 digits. Stored as a hash only; Buddy Punch PINs cannot be carried over.
              </div>
              <input
                value={pinValue} inputMode="numeric" autoComplete="off"
                onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-40 rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2.5 text-center text-lg tracking-[0.4em] text-tcs-text"
                aria-label="New PIN"
              />
              {/* ⛔ THE CODE ON ITS OWN IS USELESS. Until 2026-09-22 the address
                  a worker has to open appeared nowhere in this app, so HR had
                  the code and no way to say where to put it. Nothing sends —
                  the button says copy. */}
              {pinValue.length >= 4 && (
                <div className="border-l-[3px] border-st-warn-border bg-tcs-surface px-3 py-2">
                  <div className="text-xs font-semibold text-tcs-text">Send them this — link and code together</div>
                  <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-tcs-border bg-tcs-raised px-3 py-2 text-[12px] text-tcs-text">
{joiningMessage(pinWorker.preferred_language === 'en' ? 'en' : 'es',
                pinWorker.preferred_name || pinWorker.first_name, pinValue)}
                  </pre>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="secondary"
                      onClick={() => {
                        void navigator.clipboard.writeText(joiningMessage(
                          pinWorker.preferred_language === 'en' ? 'en' : 'es',
                          pinWorker.preferred_name || pinWorker.first_name, pinValue,
                        )).then(() => setPinCopied(true), () => setPinCopied(false));
                      }}>
                      {pinCopied ? 'Copied ✓' : 'Copy the message'}
                    </Button>
                    <span className="text-[11px] text-tcs-text-muted">
                      In {pinWorker.preferred_language === 'en' ? 'English' : 'Spanish'} · {clockUrlSpoken()}
                    </span>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setPinWorker(null)}>Cancel</Button>
                <Button size="sm" loading={pinBusy} disabled={pinValue.length < 4} onClick={() => void submitPin()}>
                  Set PIN
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
